"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SaveButtonSpinner } from "@/components/save-button-spinner";
import { CheckCircle } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useCurrentOrganization } from "@/hooks/spacetime";
import { useState } from "react";
import { toast } from "sonner";
import { fetchJson } from "@/services/api-client";
import { showApiErrorToast } from "@/lib/api-error-toast";

const PITCH_DECK_URL = "https://pitch.com/v/early-adopter-program-glaci-nncqrn";

const SNOW_LAYERS = [
  "animate-snowflake-slow-1 size-1 opacity-40",
  "animate-snowflake-2 size-0.5 opacity-60",
  "animate-snowflake-slow-2 size-1 opacity-30",
  "animate-snowflake-3 size-0.5 opacity-50",
];

interface EarlyAdopterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EarlyAdopterDialog({
  open,
  onOpenChange,
}: EarlyAdopterDialogProps) {
  const { organization } = useCurrentOrganization();
  const organizationName = organization?.name || "Your Organization";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Check if already requested from persisted state
  const hasAlreadyRequested = organization?.hasRequestedGlaciAccess ?? false;

  const handleRequestAccess = async () => {
    if (!organization?.id) {
      toast.error("Unable to submit request", {
        description: "Organization not found. Please try again.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // /api/glaci/request-access sends the sales@libum.io notification
      // email, then calls set_glaci_access_requested as caller (only after
      // the email succeeds — legacy email-first ordering), so
      // `organization.hasRequestedGlaciAccess` does update live from this
      // call. Local `isSubmitted` state is kept anyway: it flips
      // synchronously on this call's own success, ahead of whatever latency
      // the live subscription update takes to arrive.
      await fetchJson<unknown>(
        "/api/glaci/request-access",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ organizationId: organization.id }),
        },
        { fallbackErrorMessage: "Failed to submit request" },
      );

      setIsSubmitted(true);

      toast.success("Request submitted!", {
        description:
          "Our team will review your request and get back to you soon.",
      });
    } catch (error) {
      showApiErrorToast("Failed to submit request", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset state when dialog closes
      setIsSubmitted(false);
    }
    onOpenChange(newOpen);
  };

  const requestSubmitted = isSubmitted || hasAlreadyRequested;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[90dvh] gap-0 overflow-y-auto bg-card p-0 sm:max-w-lg"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg bg-gradient-to-b from-sky-100/40 via-transparent to-transparent dark:from-sky-900/20"
        >
          <div className="absolute inset-0 motion-reduce:hidden">
            {Array.from({ length: 16 }, (_, index) => (
              <span
                key={index}
                className={`absolute top-0 rounded-full bg-sky-400 dark:bg-sky-100 ${SNOW_LAYERS[index % SNOW_LAYERS.length]}`}
                style={{
                  left: `${(index * 23 + 3) % 100}%`,
                  animationDelay: `${-index * 1.7}s`,
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative px-6 pb-6 pt-12 sm:px-10">
          <div className="mb-8 flex justify-center">
            <Image
              src="/logos/glaci_dark.svg"
              alt="Glaci"
              width={140}
              height={48}
              className="hidden h-12 w-auto dark:block"
            />
            <Image
              src="/logos/glaci_light.svg"
              alt="Glaci"
              width={140}
              height={48}
              className="block h-12 w-auto dark:hidden"
            />
          </div>

          <DialogHeader className="space-y-3 text-center">
            <DialogTitle className="text-xl font-bold leading-snug text-gray-1100 dark:text-gray-100 sm:text-2xl">
              <Image
                src="/emojis/seedling.svg"
                alt=""
                width={24}
                height={24}
                className="mr-2 inline-block size-5 align-[-0.125em] sm:size-6"
              />
              Join our Early Adopter Program
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed sm:text-base">
              {requestSubmitted ? (
                "Thanks for your interest in Glaci. Our team will be in touch with next steps."
              ) : (
                <>
                  Be among the first credit unions to use Glaci. Explore our{" "}
                  <Link
                    href={PITCH_DECK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-brand-secondary hover:underline focus-visible:outline-brand-primary"
                  >
                    pitch deck
                    <span className="sr-only"> (opens in a new tab)</span>
                  </Link>{" "}
                  and request access for your organization.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-7 flex flex-col gap-2">
            {requestSubmitted ? (
              <div
                role="status"
                className="flex min-h-10 items-center justify-center gap-2 text-sm font-medium text-gray-1100 dark:text-gray-100"
              >
                <CheckCircle
                  className="size-4 text-emerald-600 dark:text-emerald-400"
                  aria-hidden="true"
                />
                Request Submitted
              </div>
            ) : (
              <div className="flex items-center">
                <SaveButtonSpinner show={isSubmitting} margin="right" />
                <Button
                  size="lg"
                  className="h-auto min-h-10 min-w-0 flex-1 whitespace-normal break-words px-4 text-sm"
                  onClick={handleRequestAccess}
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                >
                  <span className="min-w-0">
                    Request Access for {organizationName}
                  </span>
                </Button>
              </div>
            )}
            <Button
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              className="w-full text-muted-foreground"
            >
              {requestSubmitted ? "Close" : "Maybe Later"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
